import { ChangeEvent, FocusEvent, useEffect, useState } from 'react';
import {
  applyDateMask,
  formatDdMmYyyy,
  parseStrictDdMmYyyy,
} from '@/components/form/_primitives/date/dateInputUtils.ts';

type UseMaskedDateInputResult = {
  text: string;
  handleTextChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
  restorePartialText: () => void;
};

export const useMaskedDateInput = (
  value: Date | undefined,
  onChange: (date: Date | undefined) => void,
  externalOnBlur?: (e: FocusEvent<HTMLInputElement>) => void,
): UseMaskedDateInputResult => {
  const [text, setText] = useState(() => formatDdMmYyyy(value));

  useEffect(() => {
    const parsed = parseStrictDdMmYyyy(text);
    const sameDate =
      (value === undefined && parsed === undefined) ||
      (value !== undefined &&
        parsed !== undefined &&
        value.getTime() === parsed.getTime());
    if (!sameDate) {
      setText(formatDdMmYyyy(value));
    }
    // text is an output of this effect — don't depend on it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const restorePartialText = () => {
    if (text.length === 0) return;
    if (text.length < 10 || !parseStrictDdMmYyyy(text)) {
      setText(formatDdMmYyyy(value));
    }
  };

  const handleTextChange = (e: ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setText(masked);
    if (masked.length === 10) {
      const parsed = parseStrictDdMmYyyy(masked);
      onChange(parsed);
    } else if (masked === '') {
      onChange(undefined);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    restorePartialText();
    externalOnBlur?.(e);
  };

  return { text, handleTextChange, handleBlur, restorePartialText };
};
